import ClearIcon from "@mui/icons-material/Clear";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Aircraft } from "../../../../types";
import { AutocompleteAircraft } from "../../HangarSettings/AutocompleteAircraft";
import { useReferenceAircraftState } from "./stores/useReferenceAircraftState";

type Props = {
  open: boolean;
  onClose: () => void;
  rampWidth: number;
  rampDepth: number;
};

export const ReferenceAircraftDialog: React.FC<Props> = ({
  open,
  onClose,
  rampWidth,
  rampDepth,
}) => {
  const { t } = useTranslation();
  const {
    aircraft: currentAircraft,
    setAircraft,
    setPosition,
    clear,
  } = useReferenceAircraftState();

  const [selectedAircraft, setSelectedAircraft] =
    React.useState<Aircraft | null>(currentAircraft);

  // Sync with store when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedAircraft(currentAircraft);
    }
  }, [open, currentAircraft]);

  const handleApply = () => {
    if (selectedAircraft) {
      setAircraft(selectedAircraft);
      // Set initial position to center of ramp
      setPosition({
        x: rampWidth / 2,
        y: rampDepth / 2,
        angle: 0,
      });
    }
    onClose();
  };

  const handleClear = () => {
    clear();
    setSelectedAircraft(null);
    onClose();
  };

  const handleSelect = (aircraft: Aircraft | null) => {
    setSelectedAircraft(aircraft);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t(
          "settings.rampsSettings.rampEditor.referenceAircraft.dialog.title",
          "Select Reference Aircraft"
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              "settings.rampsSettings.rampEditor.referenceAircraft.dialog.description",
              "Choose an aircraft to display as a scale reference on the canvas. Drag to move, press R to rotate."
            )}
          </Typography>

          <AutocompleteAircraft
            label={t(
              "settings.rampsSettings.rampEditor.referenceAircraft.dialog.searchLabel",
              "Search Aircraft"
            )}
            onSelect={handleSelect}
            defaultSelected={selectedAircraft}
          />

          {selectedAircraft && (
            <Box
              sx={{
                p: 2,
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {t(
                  "settings.rampsSettings.rampEditor.referenceAircraft.dialog.currentAircraft",
                  "Selected Aircraft"
                )}
              </Typography>
              <Typography variant="body1">
                {selectedAircraft.make} {selectedAircraft.model}
              </Typography>
              {(selectedAircraft.wingspan || selectedAircraft.length) && (
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "settings.rampsSettings.rampEditor.referenceAircraft.dialog.dimensions",
                    "{{wingspan}}ft wingspan x {{length}}ft length",
                    {
                      wingspan: selectedAircraft.wingspan?.toFixed(0) || "?",
                      length: selectedAircraft.length?.toFixed(0) || "?",
                    }
                  )}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {currentAircraft && (
          <Button
            onClick={handleClear}
            color="error"
            startIcon={<ClearIcon />}
            sx={{ mr: "auto" }}
          >
            {t(
              "settings.rampsSettings.rampEditor.referenceAircraft.dialog.clearButton",
              "Remove Reference"
            )}
          </Button>
        )}
        <Button onClick={onClose}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={!selectedAircraft}
        >
          {t(
            "settings.rampsSettings.rampEditor.referenceAircraft.dialog.applyButton",
            "Apply"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
